import React, {FC, useMemo} from 'react'
import {RequisitionDisplay, RequisitionLine} from '../core/_models'
import {DocBlock, DocField, DocFieldGrid, DocNote} from './ProcurementDocumentSections'
import ProcurementAttachmentPrintList from './ProcurementAttachmentPrintList'
import {useProcurementBrandingAssets} from './ProcurementDocumentBranding'
import {
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getLinkedDefectDisplay,
  getSubcomponentMakerFromLine,
  getEquipmentMakerFromLine,
  getEquipmentModelFromLine,
  getSubcomponentModelFromLine,
  getSubcomponentNameFromLine,
  resolveRequisitionViewContext,
  uniqueJoinedValues,
} from '../core/technicalItemContext'

interface RequisitionLECRDocumentProps {
  requisition: RequisitionDisplay
  viewLines: RequisitionLine[]
  viewLinesLoading?: boolean
  canEditQuantities?: boolean
  quantityDrafts?: Record<number, string>
  updatingLineId?: number | null
  onQuantityDraftChange?: (lineId: number, value: string) => void
  onSaveLineQuantity?: (lineId: number) => void
  attachments?: Array<{fileName: string; url?: string | null; sizeBytes?: number | null}>
}

type GroupedLine = {group: string; lines: RequisitionLine[]}

const RequisitionLECRDocument: FC<RequisitionLECRDocumentProps> = ({
  requisition,
  viewLines,
  viewLinesLoading = false,
  canEditQuantities = false,
  quantityDrafts = {},
  updatingLineId,
  onQuantityDraftChange,
  onSaveLineQuantity,
  attachments = [],
}) => {
  const requisitionAny = requisition as any
  const context = resolveRequisitionViewContext(
    requisitionAny?.requestFor,
    requisitionAny?.category?.name || requisition.category
  )
  const isTechnical = context === 'TECHNICAL'
  const isServices = context === 'SERVICES'
  const groupedLines = useMemo<GroupedLine[]>(() => {
    const map = new Map<string, RequisitionLine[]>()
    viewLines.forEach((line) => {
      const key = (line.subcomponentClubbing || 'General').trim() || 'General'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(line)
    })
    return Array.from(map.entries()).map(([group, lines]) => ({group, lines}))
  }, [viewLines])

  const displayValue = (value: unknown, fallback = '-') => {
    const text = String(value ?? '').trim()
    return text ? text : fallback
  }
  const matchesLabel = (value: unknown, label: string) =>
    String(value ?? '').trim().toLowerCase() === label.trim().toLowerCase()
  const categoryName = requisitionAny?.category?.name || requisition.category || '-'
  const subCategoryName = requisitionAny?.inventoryItemSubCategoryName || '-'
  const vesselName =
    requisitionAny?.vessel?.fleet_name ||
    requisitionAny?.vessel?.name ||
    requisitionAny?.vesselName ||
    '-'
  const companyName =
    requisitionAny?.category?.cgaid?.name ||
    requisitionAny?.companyName ||
    requisitionAny?.company?.name ||
    '-'
  const {logoSrc: companyLogoUrl, watermarkSrc: companyWatermarkUrl, ready: brandingReady} =
    useProcurementBrandingAssets(
      requisitionAny?.companyLogoUrl ||
        requisitionAny?.logoUrl ||
        requisitionAny?.company?.logoUrl ||
        requisitionAny?.category?.caid?.logoUrl ||
        requisitionAny?.category?.cgaid?.logoUrl,
      requisitionAny?.companyWatermarkUrl ||
        requisitionAny?.watermarkUrl ||
        requisitionAny?.company?.watermarkUrl ||
        requisitionAny?.category?.caid?.watermarkUrl ||
        requisitionAny?.category?.cgaid?.watermarkUrl,
      requisitionAny?.category?.cgaid
    )
  const requisitionTitle =
    (requisition as any).name ||
    (requisition as any).requisitionName ||
    requisition.title ||
    '-'
  const linkedDefectDisplay = getLinkedDefectDisplay(requisition as any) || '-'
  const equipmentSummary = uniqueJoinedValues(viewLines.map((line: any) => getEquipmentNameFromLine(line)))
  const raisedDateDisplay = requisition.raisedAt ? String(requisition.raisedAt).slice(0, 10) : '-'
  const rootClassName = `procurement-doc lecr-doc${companyWatermarkUrl ? ' procurement-has-watermark' : ''}`

  return (
    <div className={rootClassName} data-export-ready={brandingReady ? 'true' : 'false'}>
      {companyWatermarkUrl ? (
        <div className='procurement-doc-watermark' aria-hidden='true'>
          <img
            src={companyWatermarkUrl}
            alt=''
            className='procurement-doc-watermark-image'
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
          />
        </div>
      ) : null}
      <div className='procurement-doc-content'>
        {companyLogoUrl ? (
          <div className='procurement-doc-brand-header mb-3'>
            <img
              src={companyLogoUrl}
              alt='Company Logo'
              className='procurement-doc-brand-logo'
              crossOrigin='anonymous'
              data-pdf-logo='true'
            />
          </div>
        ) : null}

        <DocBlock title='Requisition Header'>
          <DocFieldGrid columns={4}>
            <DocField label='Requisition Number' value={requisition.reqNumber} />
            <DocField label='Linked Defect' value={linkedDefectDisplay} />
            <DocField label='Requisition Title' value={requisitionTitle} />
            <DocField label='Category' value={categoryName} />
            <DocField label='Sub-category' value={subCategoryName} />
            <DocField label='Company' value={companyName} />
            <DocField label='Region' value={requisition.regionName || '-'} />
            <DocField label='Vessel' value={vesselName} />
            {isTechnical && <DocField label='Equipment' value={equipmentSummary} />}
            <DocField label='Raised Date' value={raisedDateDisplay} />
            <DocField label='Delivery Port' value={requisition.requestedPort || '-'} />
            <DocField label='Delivery Date' value={requisition.requiredDate || '-'} />
          </DocFieldGrid>
          <div className='mt-3'><DocNote title='Description'>{requisitionAny.description || '-'}</DocNote></div>
        </DocBlock>

        <DocBlock title='Subcatalogue / Item Lines'>
          {viewLinesLoading ? (
            <div className='text-muted'>Loading line items...</div>
          ) : groupedLines.length === 0 ? (
            <div className='text-muted'>No line items available.</div>
          ) : (
            groupedLines.map((group, gIdx) => (
              <div key={`${group.group}-${gIdx}`} className='mb-4'>
                <div className='bg-light-primary text-primary fw-bold px-3 py-2 rounded mb-2'>
                  {group.group}
                </div>
                <div className='table-responsive'>
                  <table
                    className={`table table-bordered align-middle mb-0 doc-tight ${
                      canEditQuantities ? 'procurement-edit-table' : 'procurement-print-table'
                    }`}
                  >
                    <thead className='table-light'>
                      <tr>
                        <th style={{width: '56px'}}>No</th>
                        <th>Item Description</th>
                        {!isServices && <th style={{width: '96px'}}>ROB</th>}
                        <th style={{width: canEditQuantities ? '220px' : '110px'}}>Requested Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.lines.map((line, idx) => {
                        const drawingNo =
                          (line as any).drawingNumber || (line as any).part?.drawNo || '-'
                        const partNo =
                          (line as any).partNumber ||
                          (line as any).part?.partNo ||
                          (line as any).part?.code ||
                          '-'
                        const robValue =
                          (line as any).rob ??
                          (line as any).part?.rob ??
                          (line as any).part?.robQuantity ??
                          '-'
                        const serviceHeadName = displayValue(
                          (line as any)?.inventoryItemHead?.name ||
                            (line as any)?.inventoryItemHeadName ||
                            requisitionAny?.inventoryItemHead?.name ||
                            requisitionAny?.inventoryItemHead ||
                            requisitionAny?.category?.name
                        )
                        const serviceSubHeadName = displayValue(
                          (line as any)?.inventoryItemSubHead?.name ||
                            (line as any)?.inventoryItemSubHeadName ||
                            requisitionAny?.inventoryItemSubHead?.name ||
                            requisitionAny?.inventoryItemSubHead ||
                            requisitionAny?.inventoryItemSubCategoryName
                        )
                        const manualServiceRemark = String((line as any)?.manualItemName || '').trim()
                        const notesServiceRemark = String(
                          (line as any)?.notes || (line as any)?.remarks || ''
                        ).trim()
                        const serviceRemark = displayValue(
                          manualServiceRemark && !matchesLabel(manualServiceRemark, serviceSubHeadName)
                            ? manualServiceRemark
                            : notesServiceRemark || manualServiceRemark || getItemDisplayNameFromLine(line, idx)
                        )
                        const lineAttachments = (line as any).attachments || []
                        const draftValue =
                          line.id != null
                            ? quantityDrafts[line.id] ?? String(line.quantity ?? '')
                            : String(line.quantity ?? '')
                        return (
                          <React.Fragment key={`${group.group}-${line.id ?? idx}`}>
                            <tr className='procurement-avoid-break'>
                              <td>{idx + 1}</td>
                              <td>
                                {isServices ? (
                                  <>
                                    <div className='procurement-line-detail'>Head: {serviceHeadName}</div>
                                    <div className='procurement-line-detail'>Subhead: {serviceSubHeadName}</div>
                                    <div className='procurement-line-detail'>Remark: {serviceRemark}</div>
                                  </>
                                ) : (
                                  <>
                                    <div>{getItemDisplayNameFromLine(line, idx)}</div>
                                    <div className='procurement-line-detail'>
                                      {isTechnical
                                        ? `Part: ${(line as any).partName || (line as any).part?.name || '-'} | Part No: ${partNo} | Drawing: ${drawingNo}`
                                        : `Category: ${categoryName} | Sub-category: ${subCategoryName}`}
                                    </div>
                                    {isTechnical ? (
                                      Boolean((line as any).part?.isSpare) ? (
                                        <>
                                          <div className='procurement-line-detail'>
                                            Equipment: {(line as any).equipmentName || getEquipmentNameFromLine(line) || '-'} | Equipment Maker:{' '}
                                            {getEquipmentMakerFromLine(line) || '-'} | Equipment Model: {getEquipmentModelFromLine(line) || '-'}
                                          </div>
                                          <div className='procurement-line-detail'>
                                            Subcomponent: {(line as any).subcomponentName || getSubcomponentNameFromLine(line) || '-'} | Subcomponent Maker:{' '}
                                            {getSubcomponentMakerFromLine(line) || '-'} | Subcomponent Model: {getSubcomponentModelFromLine(line) || '-'}
                                          </div>
                                        </>
                                      ) : (
                                        <div className='procurement-line-detail'>
                                          Equipment: {(line as any).equipmentName || getEquipmentNameFromLine(line) || '-'} | Subcomponent:{' '}
                                          {(line as any).subcomponentName || getSubcomponentNameFromLine(line) || '-'} | Maker:{' '}
                                          {(line as any).maker || getSubcomponentMakerFromLine(line) || '-'}
                                        </div>
                                      )
                                    ) : null}
                                  </>
                                )}
                                {!isServices && line.notes ? (
                                  <div className='procurement-line-note'>
                                    <span className='text-muted fw-semibold'>Item Remarks:</span> {line.notes}
                                  </div>
                                ) : null}
                              </td>
                              {!isServices && <td>{robValue}</td>}
                              <td className={canEditQuantities ? 'procurement-edit-qty-cell' : undefined}>
                                {canEditQuantities && line.id ? (
                                  <div className='procurement-edit-qty-actions'>
                                    <input
                                      type='number'
                                      min='1'
                                      step='1'
                                      className='form-control form-control-sm'
                                      style={{minWidth: '96px'}}
                                      value={draftValue}
                                      onChange={(e) => onQuantityDraftChange?.(line.id as number, e.target.value)}
                                    />
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-light-primary flex-shrink-0 text-nowrap'
                                      disabled={
                                        updatingLineId === line.id ||
                                        draftValue === String(line.quantity ?? '')
                                      }
                                      onClick={() => onSaveLineQuantity?.(line.id as number)}
                                    >
                                      {updatingLineId === line.id ? '...' : 'Save'}
                                    </button>
                                  </div>
                                ) : (
                                  <>{line.quantity ?? '-'}</>
                                )}
                              </td>
                            </tr>
                            {lineAttachments.length > 0 && (
                              <tr>
                                <td colSpan={isServices ? 3 : 4} className='bg-light'>
                                  <span className='text-muted fw-semibold d-block mb-2'>Item Attachments</span>
                                  <ProcurementAttachmentPrintList
                                    attachments={lineAttachments.map((attachment: any) => ({
                                      id: attachment?.id ?? null,
                                      registryId: attachment?.registryId ?? attachment?.id ?? null,
                                      attachmentKey: attachment?.attachmentKey ?? null,
                                      fileName: attachment?.fileName || attachment?.name || 'Attachment',
                                      url: attachment?.url || attachment?.path || '',
                                      viewUrl: attachment?.viewUrl || null,
                                      downloadUrl: attachment?.downloadUrl || null,
                                      sizeBytes: attachment?.sizeBytes ?? attachment?.size ?? null,
                                      available: attachment?.available ?? null,
                                    }))}
                                    emptyText='No item attachments.'
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </DocBlock>

        <DocBlock title='Attachments'>
          <ProcurementAttachmentPrintList attachments={attachments} />
        </DocBlock>
      </div>
    </div>
  )
}

export default RequisitionLECRDocument
